import './src/styles/normalize.css';
import './src/styles/global.css';
import './src/styles/typography.css';
import './src/styles/navigation.css';
import './src/styles/selector.css';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import './src/styles/cookie-consent.css';

export const onClientEntry = async () => {
  if (typeof IntersectionObserver === "undefined") {
    await import("intersection-observer")
  }
}

const ANCHOR_SCROLL_OFFSET = -100; // fixed header height, same as gatsby-plugin-anchor-links offset

/**
 * Instant (non-animated) jump to a section after cross-page navigation.
 * Triggered by navigating with state: { preventScroll: true, scrollTo: '<element id>' }.
 */
const scrollToAnchorInstantly = (id, attempt = 0) => {
  const target = document.getElementById(id);

  if (!target) {
    // section may not be mounted in the very first frame after navigation
    if (attempt < 20) window.requestAnimationFrame(() => scrollToAnchorInstantly(id, attempt + 1));
    return;
  }

  const top = target.getBoundingClientRect().top + window.pageYOffset + ANCHOR_SCROLL_OFFSET;

  try {
    window.scrollTo({ top, behavior: 'instant' });
  } catch (e) {
    window.scrollTo(0, top);
  }
};

export const onRouteUpdate = ({ location }) => {
  const anchor = location.state && location.state.scrollTo;

  if (anchor) {
    window.requestAnimationFrame(() => scrollToAnchorInstantly(anchor));
  }
};

export const shouldUpdateScroll = ({ routerProps: { location }, getSavedScrollPosition }) => {
  if (location.state && location.state.preventScroll === true) {
    return false;
  }

  if (location.hash) {
    window.history.scrollRestoration = 'auto';
    return true;
  }

  window.history.scrollRestoration = 'manual';

  const currentPosition = getSavedScrollPosition(location, location.key);
  const top = currentPosition ? currentPosition[1] : 0;

  window.setTimeout(() => {
    window.requestAnimationFrame(() => {
      try {
        window.scrollTo({ top, behavior: 'instant' });
      } catch (e) {
        window.scrollTo(top);
      }
    });
  }, 0);

  return false;
};

export const onInitialClientRender = () => {
  (async () => {
    const CookieConsent = await import("vanilla-cookieconsent");

    CookieConsent.run({
      cookie: {
          name: "cc_debolinska_cz",
          domain: window.location.hostname,
          path: '/',
          secure: true,
          expiresAfterDays: 182,
          sameSite: 'Lax',
          useLocalStorage: false
      },
      guiOptions: {
          consentModal: {
              layout: "cloud inline",
              position: "bottom center",
              equalWeightButtons: true,
              flipButtons: false
          },
          preferencesModal: {
              layout: "box",
              position: "right",
              equalWeightButtons: true,
              flipButtons: true
          }
      },
      categories: {
          necessary: {
              readOnly: true
          },
          analytics: {},
          ads: {}
      },
      language: {
        default: "cs",
        autoDetect: "browser",
        translations: {
          cs: {
            consentModal: {
                title: "Tato webová stránka používá cookies",
                description: 'Tyto stránky potřebují Váš <a href="https://debolinska.cz/data/ochrana-osobnich-udaju.pdf" target="_blank" rel="noreferrer">souhlas</a> k využití jednotlivých dat, aby Vám mimo jiné mohli ukazovat informace týkající se Vašich zájmů. Souhlas udělíte kliknutím na políčko „Povolit vše“.',
                acceptAllBtn: "Povolit vše",
                acceptNecessaryBtn: "Odmítnout",
                showPreferencesBtn: "Detaily",
            },
            preferencesModal: {
                title: "Nastavení cookies",
                acceptAllBtn: "Povolit vše",
                acceptNecessaryBtn: "Odmítnout",
                savePreferencesBtn: "Povolit vybrané",
                closeIconLabel: "Zavřít",
                serviceCounterLabel: "Service|Services",
                sections: [
                    {
                        title: "Cookies - Nastavení",
                        description: 'Zde máte možnost přizpůsobit soubory cookie podle kategorií, v souladu s vlastními preferencemi. <a href="https://debolinska.cz/data/ochrana-osobnich-udaju.pdf" target="_blank" rel="noreferrer">Více informací</a>.'
                    },
                    {
                        title: "Technické cookies",
                        description: "Technické cookies jsou nezbytné pro správné fungování webu a všech funkcí, které nabízí. Jsou odpovědné mj. za uchovávání produktů v košíku, zobrazování seznamu oblíbených výrobků (schránka), působení filtrů, nákupní proces a ukládání nastavení soukromí. Nepožadujeme Váš souhlas s využitím technických cookies na našem webu. Z tohoto důvodu technické cookies nemohou být individuálně deaktivovány nebo aktivovány.",
                        linkedCategory: "necessary"
                    },

                    {
                        title: "Analytické cookies",
                        description: "Analytické cookies nám umožňují měření výkonu našeho webu a našich reklamních kampaní. Jejich pomocí určujeme počet návštěv a zdroje návštěv našich internetových stránek. Data získaná pomocí těchto cookies zpracováváme souhrnně, bez použití identifikátorů, které ukazují na konkrétní uživatelé našeho webu. Pokud vypnete používání analytických cookies ve vztahu k Vaší návštěvě, ztrácíme možnost analýzy výkonu a optimalizace našich opatření.",
                        linkedCategory: "analytics"
                    },
                    {
                        title: "Reklamní cookies",
                        description: "Reklamní cookies používáme my nebo naši partneři, abychom Vám mohli zobrazit vhodné obsahy nebo reklamy jak na našich stránkách, tak na stránkách třetích subjektů. Díky tomu můžeme vytvářet profily založené na Vašich zájmech, tak zvané pseudonymizované profily. Na základě těchto informací není zpravidla možná bezprostřední identifikace Vaší osoby, protože jsou používány pouze pseudonymizované údaje. Pokud nevyjádříte souhlas, nebudete příjemcem obsahů a reklam přizpůsobených Vašim zájmům.",
                        linkedCategory: "ads"
                    },
                    {
                        title: "Více informací",
                        description: 'Máte-li jakýkoliv dotaz týkající se našich zásad týkajících se souborů cookie a vašich voleb, prosím <a href="mailto:info@estyholding.cz">Kontaktujte nás</a>.'
                    }
                ]
            }
        }
      }
    },
      onModalReady: ({ modalName, modal }) => {
        if (modalName === "consentModal") {
          modal.querySelector('[data-role="necessary"]')?.classList.add("cm__btn--secondary");
          modal.querySelector('[data-cc="accept-necessary"]')?.classList.add("btn", "btn-ghost");
          modal.querySelector('[data-cc="show-preferencesModal"]')?.classList.add("link", "underline");

          (modal.querySelector(".cm__title") || modal.querySelector("h2"))?.classList.add(
            "es-h5",
          );
        }

        if (modalName === "preferencesModal") {
          modal.querySelector('[data-role="necessary"]')?.classList.add("pm__btn--secondary");
        }
      },
});

  })();
};